import { useCallback, useMemo } from 'react';
import { IconInfoBold, Text } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import styled from '@emotion/styled';

import { isZigzag, useZigzag } from '@common/device-manager';
import { PurchaseHistoryCardContainer, PurchaseHistoryCardItem } from '@common/product-card';
import { PopupViewType, usePopupView } from '@domains/pdp-common';

import { useActualSizeUBL } from '../../log/useActualSizeUBL';
import { ActualSizeTabName, SIZE_TAB, useActualSizeItemService } from '../../services/actual-size';
import { SizeInfoItem } from '../../services/actual-size/types';
import { useSizeInfoAgreementListener } from '../../services/useSizeInfoAgreementListener';
import { SizeInfoTabs } from './SizeInfoTabs';
import { SizeMeasurementsTable } from './SizeMeasurementsTable';

interface ActualSizeItemSectionProps {
  rawItem: SizeInfoItem;
  /** 첫 번째 아이템인지 여부 (UBL impression 등 처리) */
  isFirst?: boolean;
  /** 노출 여부 확인됨 */
  isRevealed?: boolean;
}

/**
 * 개별 사이즈 카테고리 아이템을 렌더링하는 섹션 컴포넌트
 * 각 size_category_id별로 독립적인 상태와 UI를 관리합니다.
 */
export const ActualSizeItemSection: React.FC<ActualSizeItemSectionProps> = ({ rawItem, isFirst, isRevealed }) => {
  const {
    item,
    sizeTable,
    activeTab,
    onTabClick,
    myPurchaseData,
    selectedPurchaseId,
    onPurchaseCardClick,
    selectedPurchaseImage,
    uiState,
    mySizeRowProps,
    onCompareClick,
    sizeCategoryId,
    handleSelectMyPurchaseFromMessage,
    handleAgreeSizeInfo,
    handleCompareDirectInput,
  } = useActualSizeItemService(rawItem);

  // Broadcast 리스너 - 각 테이블별로 독립 처리
  useSizeInfoAgreementListener({
    onAgree: (payload) => {
      // 동의는 전역 상태 → 첫 번째 섹션에서만 API 호출 (중복 방지)
      if (!isFirst) return;
      if (payload.type !== 'compare') return;
      handleAgreeSizeInfo();
    },
    onSelectMyPurchase: (catalogProductId, uniqueKey) => {
      // 구매내역 선택: 해당 uniqueKey가 현재 테이블의 데이터에 있는 경우에만 처리
      const hasUniqueKey = myPurchaseData?.some((item) => item.uniqueKey === uniqueKey);
      if (!hasUniqueKey) return;
      handleSelectMyPurchaseFromMessage(catalogProductId, uniqueKey);
    },
    onCompareDirectInput: () => {
      // 직접입력 완료: 첫 번째 섹션에서만 refetch (중복 방지, react-query 캐시 공유)
      if (!isFirst) return;
      handleCompareDirectInput();
    },
  });

  const { showPopup } = usePopupView();
  const isZigzagApp = useZigzag();

  const {
    myPurchasedTabClickUBL,
    mySizeTabClickUBL,
    myPurchasedClickUBL,
    myPurchasedImpressionUBL,
    mySizeInputButtonClickUBL,
  } = useActualSizeUBL();

  const purchaseData = myPurchaseData || [];

  const handleMorePurchaseClick = useCallback(() => {
    const queryParams: Record<string, string | number> = {};
    if (sizeCategoryId) {
      queryParams.size_category_id = sizeCategoryId;
    }

    showPopup(PopupViewType.MY_PURCHASE_LIST, undefined, Object.keys(queryParams).length > 0 ? queryParams : undefined);
  }, [sizeCategoryId, showPopup]);

  const handleProductClick = useCallback((cardItem: PurchaseHistoryCardItem) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const targetId = (cardItem as any).catalog_product_id || cardItem.id;
    if (!targetId) return;

    if (isZigzag()) {
      const redirectUrl = `zigzag://open/product_detail?browsing_type=NATIVE_BROWSER&catalog_product_id=${targetId}`;
      location.assign(redirectUrl);
    } else {
      location.assign(`${location.origin}/catalog/products/${targetId}`);
    }
  }, []);

  const handleTabClick = useCallback(
    (tab: ActualSizeTabName) => {
      if (tab === SIZE_TAB.MY_PURCHASE) {
        myPurchasedTabClickUBL();
      } else if (tab === SIZE_TAB.DIRECT_INPUT) {
        mySizeTabClickUBL();
      }
      onTabClick(tab);
    },
    [myPurchasedTabClickUBL, mySizeTabClickUBL, onTabClick],
  );

  const handlePurchaseCardClick = useCallback(
    (purchasedProductId: string): boolean => {
      myPurchasedClickUBL({ purchased_product_id: purchasedProductId });
      return onPurchaseCardClick(purchasedProductId);
    },
    [myPurchasedClickUBL, onPurchaseCardClick],
  );

  const handleCompareClick = useCallback(() => {
    mySizeInputButtonClickUBL();
    onCompareClick();
  }, [mySizeInputButtonClickUBL, onCompareClick]);

  const mySizeRowWithUBL = useMemo(() => {
    if (!mySizeRowProps?.onAction) return mySizeRowProps;
    return {
      ...mySizeRowProps,
      onAction: () => {
        mySizeInputButtonClickUBL();
        mySizeRowProps.onAction?.();
      },
    };
  }, [mySizeRowProps, mySizeInputButtonClickUBL]);

  // 구매내역 노출 시 impression UBL
  useMemo(() => {
    if (isRevealed && uiState.showMyPurchaseCarousel && purchaseData.length > 0) {
      purchaseData.forEach((purchase: PurchaseHistoryCardItem) => {
        myPurchasedImpressionUBL({ purchased_product_id: purchase.id });
      });
    }
  }, [isRevealed, uiState.showMyPurchaseCarousel, purchaseData, myPurchasedImpressionUBL]);

  if (!item) return null;

  const renderImageArea = uiState.showImageArea && (
    <ImageArea>
      <Image src={item.imageUrl} alt='실측 사이즈 정보' />
    </ImageArea>
  );

  const renderPurchaseCarousel = uiState.showMyPurchaseCarousel && (
    <CardListWrapper>
      <PurchaseHistoryCardContainer
        items={purchaseData}
        selectedId={selectedPurchaseId}
        onSelect={(cardItem) => handlePurchaseCardClick(cardItem.uniqueKey)}
        onProductClick={handleProductClick}
        onMoreClick={isZigzagApp ? handleMorePurchaseClick : undefined}
      />
    </CardListWrapper>
  );

  const renderSizeTable = uiState.showTable && (
    <TableWrapper>
      <SizeMeasurementsTable
        headers={item.headers}
        rows={item.rows}
        fixedColumnWidth={sizeTable.fixedColumnWidth}
        columnMinWidth={sizeTable.columnMinWidth}
        handleSetColRef={sizeTable.handleSetColRef}
        mySizeRow={mySizeRowWithUBL}
        compareRowImage={selectedPurchaseImage}
        highlightFlags={item.highlightFlags}
        hasCompareRow={item.hasCompareRow}
        onCompareClick={handleCompareClick}
      />
    </TableWrapper>
  );

  const renderSizeInfoTabs = uiState.showTabs && <SizeInfoTabs activeTab={activeTab} onTabClick={handleTabClick} />;

  const BETA_DESCRIPTION_LIST = [
    '가장 비슷한 사이즈가 하늘색으로 표시돼요.',
    '소재나 측정 방식에 따라 차이가 있을 수 있어요.',
    '구매내역은 베타 기능으로 일부 상품만 표시될 수 있어요.',
  ];

  const renderBetaDescription = uiState.showTabs && (
    <DescriptionContainer>
      <DescriptionHeader>
        <IconInfoBold color='iconTertiary' size={16} />
        <Text variant='Caption_12_SemiBold' color='textTertiary'>
          사이즈 비교하기 안내
        </Text>
      </DescriptionHeader>
      <DescriptionList>
        {BETA_DESCRIPTION_LIST.map((description) => (
          <li key={description}>
            <Text variant='Caption_12_Regular' color='textTertiary'>
              {description}
            </Text>
          </li>
        ))}
      </DescriptionList>
    </DescriptionContainer>
  );

  const renderDefaultDescription = !uiState.showTabs && uiState.showDescription && (
    <ContentWrapper>
      <Description>
        <DescriptionIcon>
          <IconInfoBold color='iconTertiary' size={16} />
        </DescriptionIcon>
        <ul>
          {item?.description?.map((description: string) => (
            <li key={description}>
              <Text variant='Caption_12_Regular' color='textTertiary' align='left'>
                {description}
              </Text>
            </li>
          ))}
        </ul>
      </Description>
    </ContentWrapper>
  );

  return (
    <SectionWrapper>
      <ContentWrapper>
        {renderImageArea}
        {renderSizeInfoTabs}
        {renderPurchaseCarousel}
      </ContentWrapper>
      {renderSizeTable}
      {renderBetaDescription}
      {renderDefaultDescription}
    </SectionWrapper>
  );
};

const SectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ContentWrapper = styled.div`
  padding: 0 16px;
`;

const CardListWrapper = styled.div`
  margin-top: 12px;
`;

const ImageArea = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
`;

const Image = styled.img`
  max-width: 240px;
  height: auto;
`;

const TableWrapper = styled.div`
  width: 100%;
  position: relative;
  padding-left: 16px;
`;

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 0 16px;
`;

const DescriptionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;

const DescriptionList = styled.ul`
  margin: 0;
  padding-left: 18px;
  list-style: disc;
  color: ${vars.color.textTertiary};

  li {
    line-height: 133.4%;
  }

  li::marker {
    font-size: 10px;
  }
`;

const Description = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 2px;
`;

const DescriptionIcon = styled.span`
  position: relative;
  top: -1px;
`;
